import { useRef, useState, useEffect } from "react";
import "./LocationAutoComplete.css";

const PHOTON_URL = "https://photon.komoot.io/api/";

const BIHAR_LAT = 25.5;
const BIHAR_LON = 85.5;

const LocationAutoComplete = ({ updateMapLocation }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  };

  const fetchSuggestions = async (searchText) => {
    if (!searchText || searchText.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setStatusMsg("");
      return;
    }
    setLoading(true);
    setStatusMsg("");
    try {
      const params = new URLSearchParams({
        q: searchText,
        limit: "8",
        lang: "en",
        lat: BIHAR_LAT,
        lon: BIHAR_LON,
        zoom: "8",
      });
      params.append("osm_tag", "place:village");
      params.append("osm_tag", "place:hamlet");
      params.append("osm_tag", "place:town");
      params.append("osm_tag", "place:city");
      params.append("osm_tag", "place:suburb");
      params.append("osm_tag", "place:locality");
      params.append("osm_tag", "boundary:administrative");

      const res = await fetch(`${PHOTON_URL}?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const geojson = await res.json();
      const features = (geojson.features || []).filter(
        (f) => f.properties?.country === "India" || f.properties?.countrycode === "IN"
      );
      setSuggestions(features);
      setShowDropdown(features.length > 0);
      if (features.length === 0) setStatusMsg("No results found. Try a different name.");
    } catch (err) {
      console.error("Location search error:", err);
      setStatusMsg("Search failed. Please try again.");
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setStatusMsg("");
    updateDropdownPosition();
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchSuggestions(val), 500);
  };

  const handleFocus = () => {
    updateDropdownPosition();
    if (suggestions.length > 0) setShowDropdown(true);
  };

  const handleSelect = (feature) => {
    const p = feature.properties;
    const [lon, lat] = feature.geometry.coordinates;
    const primary = p.name || p.city || p.town || p.village || "";
    const secondary = [p.county, p.state].filter(Boolean).join(", ");
    setQuery(primary + (secondary ? ", " + secondary : ""));
    setShowDropdown(false);
    setSuggestions([]);
    setStatusMsg("");
    updateMapLocation(lat, lon);
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      clearTimeout(debounceTimer.current);
    };
  }, []);

  const getTypeLabel = (p) => {
    const typeMap = {
      village: "Village", hamlet: "Hamlet", suburb: "Area",
      town: "Town", city: "City", district: "District",
      locality: "Locality", administrative: "Admin", county: "District",
    };
    return typeMap[p.type] || typeMap[p.osm_value] || "";
  };

  return (
    <div className="loc-autocomplete-wrapper">
      <input
        ref={inputRef}
        className="loc-autocomplete-input"
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder="Search village, block, panchayat, district..."
        autoComplete="off"
      />

      {loading && <div className="loc-autocomplete-status">Searching...</div>}
      {!loading && statusMsg && (
        <div className="loc-autocomplete-status loc-status-warn">{statusMsg}</div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <ul
          className="loc-autocomplete-dropdown"
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}>
          {suggestions.map((feature, idx) => {
            const p = feature.properties;
            const primary = p.name || p.city || p.town || p.village || "Unknown";
            const secondary = [p.county, p.state].filter(Boolean).join(", ");
            const typeLabel = getTypeLabel(p);
            return (
              <li
                key={`${p.osm_id}-${idx}`}
                className="loc-autocomplete-item"
                title={[primary, secondary].filter(Boolean).join(", ")}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(feature); }}>
                <div className="loc-item-row">
                  <span className="loc-autocomplete-name">{primary}</span>
                  {typeLabel && <span className="loc-type-badge">{typeLabel}</span>}
                </div>
                {secondary && (
                  <span className="loc-autocomplete-detail">{secondary}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LocationAutoComplete;
